'use client';
import React, { useState, useRef } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import LanguageToggle from '@/components/ui/LanguageToggle';
import { createClient } from '@/lib/supabase/client';
import { Briefcase, ChevronRight, ChevronLeft, CheckCircle2, Loader2, AlertCircle, Eye, EyeOff, Mail, User, Building2, Globe, Hash, Upload, FileText, X, MapPin, Star,  } from 'lucide-react';
import { useRouter } from 'next/navigation';

const STEPS_EN = ['Account', 'Company', 'Broker Profile', 'ID Document', 'Review'];

export default function BrokerRegistrationWizard() {
  const [lang, setLang] = useState<'en' | 'ar'>('en');
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedId, setSubmittedId] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [idDocumentUrl, setIdDocumentUrl] = useState<string | null>(null);
  const [idDocumentName, setIdDocumentName] = useState<string | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const supabase = createClient();
  const router = useRouter();

  const handleLangChange = (l: 'en' | 'ar') => {
    setLang(l);
    if (typeof window !== 'undefined') localStorage.setItem('nautex_lang', l);
  };

  const { register, handleSubmit, formState: { errors }, trigger, watch, getValues } = useForm({ mode: 'onTouched' });
  const password = watch('password');

  const stepFields: Record<number, string[]> = {
    1: ['full_name', 'email', 'password', 'confirm_password'],
    2: ['company_name', 'country', 'company_type'],
    3: ['position'],
    4: [],
  };

  const handleNext = async () => {
    if (step === 4 && !idDocumentUrl) {
      toast.error('Please upload your ID document before proceeding');
      return;
    }
    const fields = stepFields[step] || [];
    if (fields.length > 0) {
      const valid = await trigger(fields);
      if (!valid) return;
    }
    setStep(s => Math.min(s + 1, STEPS_EN.length));
  };

  const handleBack = () => setStep(s => Math.max(s - 1, 1));

  const handleDocumentUpload = async (file: File) => {
    setUploadingDoc(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `id-docs/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { data, error } = await supabase.storage
        .from('member-documents')
        .upload(fileName, file, { upsert: false });
      if (error) throw error;
      const { data: urlData } = supabase.storage.from('member-documents').getPublicUrl(fileName);
      setIdDocumentUrl(urlData.publicUrl);
      setIdDocumentName(file.name);
      toast.success('Document uploaded successfully');
    } catch (err: any) {
      toast.error(err?.message || 'Upload failed. Please try again.');
    } finally {
      setUploadingDoc(false);
    }
  };

  const onSubmit = async (data: any) => {
    if (!idDocumentUrl) {
      toast.error('ID document is required');
      setStep(4);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          full_name: data.full_name,
          role_intent: 'broker',
          company_name: data.company_name,
          registration_number: data.registration_number || null,
          country: data.country,
          office_name: `${data.company_name} Brokerage`,
          office_city: data.city || null,
          id_document_url: idDocumentUrl,
          broker_profile: {
            company_type: data.company_type,
            position: data.position,
            fuel_grades: data.fuel_grades || '',
            regions: data.regions || '',
            years_experience: data.years_experience || '',
            references: data.references || '',
          },
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Registration failed');
      setSubmittedId(result.registrationRef || result.memberId?.slice(0, 8).toUpperCase() || 'BROKER');
      setSubmitted(true);
    } catch (err: any) {
      toast.error(err?.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen nautex-gradient-bg flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md text-center space-y-6">
          <div className="w-16 h-16 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mx-auto">
            <CheckCircle2 size={32} className="text-amber-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Application Submitted</h2>
            <p className="text-secondary-foreground text-sm leading-relaxed">
              Your broker registration has been submitted for review. Our compliance team will verify your credentials and activate your account within 1–2 business days.
            </p>
          </div>
          <div className="bg-card/60 border border-border rounded-xl p-4 text-left space-y-2">
            <p className="text-xs text-muted-foreground">Reference Number</p>
            <p className="font-mono font-bold text-foreground text-lg">REG-{submittedId}</p>
          </div>
          <Link href="/login" className="btn-secondary inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold w-full justify-center">
            Return to Login
          </Link>
        </div>
      </div>
    );
  }

  const inputClass = (hasError?: boolean) =>
    `w-full bg-input border ${hasError ? 'border-red-500/60' : 'border-border'} rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all`;

  const labelClass = 'block text-xs font-medium text-secondary-foreground mb-1.5';

  return (
    <div className="min-h-screen nautex-gradient-bg flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4">
        <Link href="/register" className="flex items-center gap-2">
          <AppLogo size={32} />
          <span className="font-bold text-sm text-foreground tracking-tight">NautEX</span>
        </Link>
        <LanguageToggle lang={lang} onChange={handleLangChange} />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        <div className="w-full max-w-lg">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/8 text-amber-400 text-xs font-medium mb-4">
              <Briefcase size={12} />
              Procurement Broker Registration
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Join as a Broker</h1>
            <p className="text-sm text-secondary-foreground">Raise PRs on behalf of buyer offices and earn sub-commission on every closed PO.</p>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-1 mb-8">
            {STEPS_EN.map((s, i) => (
              <React.Fragment key={i}>
                <div className={`flex items-center gap-1.5 ${i + 1 === step ? 'text-amber-400' : i + 1 < step ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border ${i + 1 === step ? 'border-amber-500/50 bg-amber-500/15' : i + 1 < step ? 'border-emerald-500/50 bg-emerald-500/15' : 'border-border bg-muted/20'}`}>
                    {i + 1 < step ? <CheckCircle2 size={12} /> : i + 1}
                  </div>
                  <span className="text-xs font-medium hidden sm:block">{s}</span>
                </div>
                {i < STEPS_EN.length - 1 && <div className={`flex-1 h-px ${i + 1 < step ? 'bg-emerald-500/40' : 'bg-border'}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* Form card */}
          <div className="bg-card/60 border border-border rounded-2xl p-6 space-y-5">
            <form onSubmit={handleSubmit(onSubmit)}>

              {/* Step 1: Account */}
              {step === 1 && (
                <div className="space-y-4">
                  <h2 className="text-base font-semibold text-foreground">Account Details</h2>
                  <div>
                    <label className={labelClass}>Full Name *</label>
                    <div className="relative">
                      <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input {...register('full_name', { required: 'Full name is required' })} placeholder="Your full legal name" className={`${inputClass(!!errors.full_name)} pl-10`} />
                    </div>
                    {errors.full_name && <p className="text-xs text-red-400 mt-1">{String(errors.full_name.message)}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Email Address *</label>
                    <div className="relative">
                      <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input {...register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' } })} type="email" placeholder="you@company.com" className={`${inputClass(!!errors.email)} pl-10`} />
                    </div>
                    {errors.email && <p className="text-xs text-red-400 mt-1">{String(errors.email.message)}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Password *</label>
                    <div className="relative">
                      <input {...register('password', { required: 'Password is required', minLength: { value: 8, message: 'Min 8 characters' } })} type={showPassword ? 'text' : 'password'} placeholder="Min 8 characters" className={`${inputClass(!!errors.password)} pr-10`} />
                      <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {errors.password && <p className="text-xs text-red-400 mt-1">{String(errors.password.message)}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Confirm Password *</label>
                    <div className="relative">
                      <input {...register('confirm_password', { required: 'Please confirm password', validate: v => v === password || 'Passwords do not match' })} type={showConfirm ? 'text' : 'password'} placeholder="Repeat password" className={`${inputClass(!!errors.confirm_password)} pr-10`} />
                      <button type="button" onClick={() => setShowConfirm(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {errors.confirm_password && <p className="text-xs text-red-400 mt-1">{String(errors.confirm_password.message)}</p>}
                  </div>
                </div>
              )}

              {/* Step 2: Company */}
              {step === 2 && (
                <div className="space-y-4">
                  <h2 className="text-base font-semibold text-foreground">Company Details</h2>
                  <div>
                    <label className={labelClass}>Legal Company Name *</label>
                    <div className="relative">
                      <Building2 size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input {...register('company_name', { required: 'Company name is required' })} placeholder="e.g. Maritime Brokers LLC" className={`${inputClass(!!errors.company_name)} pl-10`} />
                    </div>
                    {errors.company_name && <p className="text-xs text-red-400 mt-1">{String(errors.company_name.message)}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Company Type *</label>
                    <select {...register('company_type', { required: 'Company type is required' })} className={inputClass(!!errors.company_type)}>
                      <option value="">Select type</option>
                      <option value="individual_agent">Individual Agent</option>
                      <option value="brokerage_firm">Brokerage Firm</option>
                    </select>
                    {errors.company_type && <p className="text-xs text-red-400 mt-1">{String(errors.company_type.message)}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Registration Number</label>
                    <div className="relative">
                      <Hash size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input {...register('registration_number')} placeholder="Trade licence / CR number" className={`${inputClass()} pl-10`} />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Country of Incorporation *</label>
                    <div className="relative">
                      <Globe size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input {...register('country', { required: 'Country is required' })} placeholder="e.g. UAE" className={`${inputClass(!!errors.country)} pl-10`} />
                    </div>
                    {errors.country && <p className="text-xs text-red-400 mt-1">{String(errors.country.message)}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>City</label>
                    <div className="relative">
                      <MapPin size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input {...register('city')} placeholder="e.g. Dubai" className={`${inputClass()} pl-10`} />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Broker Profile */}
              {step === 3 && (
                <div className="space-y-4">
                  <h2 className="text-base font-semibold text-foreground">Broker Profile</h2>
                  <div>
                    <label className={labelClass}>Your Position / Title *</label>
                    <div className="relative">
                      <Star size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input {...register('position', { required: 'Position is required' })} placeholder="e.g. Senior Bunker Broker" className={`${inputClass(!!errors.position)} pl-10`} />
                    </div>
                    {errors.position && <p className="text-xs text-red-400 mt-1">{String(errors.position.message)}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Fuel Grades Handled</label>
                    <input {...register('fuel_grades')} placeholder="e.g. VLSFO, MGO, HFO" className={inputClass()} />
                  </div>
                  <div>
                    <label className={labelClass}>Regions Covered</label>
                    <div className="relative">
                      <MapPin size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input {...register('regions')} placeholder="e.g. Middle East, Southeast Asia" className={`${inputClass()} pl-10`} />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Years of Experience</label>
                    <input {...register('years_experience')} type="number" min="0" placeholder="e.g. 8" className={inputClass()} />
                  </div>
                  <div>
                    <label className={labelClass}>Reference Contacts (optional)</label>
                    <textarea {...register('references')} rows={3} placeholder="Names and contact details of references" className={`${inputClass()} resize-none`} />
                  </div>
                </div>
              )}

              {/* Step 4: ID Document */}
              {step === 4 && (
                <div className="space-y-4">
                  <h2 className="text-base font-semibold text-foreground">Identity Document</h2>
                  <p className="text-sm text-secondary-foreground">Upload a copy of your passport or trade licence for KYC verification.</p>
                  <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleDocumentUpload(f); }} />
                  {!idDocumentUrl ? (
                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingDoc} className="w-full border-2 border-dashed border-border rounded-xl p-8 flex flex-col items-center gap-3 hover:border-amber-500/40 transition-colors">
                      {uploadingDoc ? <Loader2 size={24} className="text-amber-400 animate-spin" /> : <Upload size={24} className="text-muted-foreground" />}
                      <span className="text-sm text-secondary-foreground">{uploadingDoc ? 'Uploading...' : 'Click to upload PDF, JPG, or PNG'}</span>
                    </button>
                  ) : (
                    <div className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                      <FileText size={20} className="text-emerald-400 flex-shrink-0" />
                      <span className="text-sm text-emerald-400 flex-1 truncate">{idDocumentName}</span>
                      <button type="button" onClick={() => { setIdDocumentUrl(null); setIdDocumentName(null); }} className="text-muted-foreground hover:text-foreground">
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 5: Review */}
              {step === 5 && (
                <div className="space-y-4">
                  <h2 className="text-base font-semibold text-foreground">Review & Submit</h2>
                  <div className="space-y-3 text-sm">
                    {[
                      { label: 'Name', value: getValues('full_name') },
                      { label: 'Email', value: getValues('email') },
                      { label: 'Company', value: getValues('company_name') },
                      { label: 'Type', value: getValues('company_type')?.replace('_', ' ') },
                      { label: 'Country', value: getValues('country') },
                      { label: 'Position', value: getValues('position') },
                      { label: 'Fuel Grades', value: getValues('fuel_grades') || '—' },
                      { label: 'Regions', value: getValues('regions') || '—' },
                      { label: 'ID Document', value: idDocumentName || '—' },
                    ].map(row => (
                      <div key={row.label} className="flex justify-between gap-4 py-2 border-b border-border/50">
                        <span className="text-muted-foreground">{row.label}</span>
                        <span className="text-foreground font-medium text-right capitalize">{row.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-amber-500/8 border border-amber-500/20 rounded-xl">
                    <AlertCircle size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-secondary-foreground">By submitting, you agree to NautEX's Terms of Service and confirm that all information provided is accurate and complete.</p>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex gap-3 mt-6">
                {step > 1 && (
                  <button type="button" onClick={handleBack} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-border text-sm font-medium text-secondary-foreground hover:text-foreground hover:border-border/80 transition-colors">
                    <ChevronLeft size={16} /> Back
                  </button>
                )}
                {step < STEPS_EN.length ? (
                  <button type="button" onClick={handleNext} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-black text-sm font-semibold transition-colors">
                    Next <ChevronRight size={16} />
                  </button>
                ) : (
                  <button type="submit" disabled={submitting} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-black text-sm font-semibold transition-colors disabled:opacity-60">
                    {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting...</> : <><CheckCircle2 size={16} /> Submit Application</>}
                  </button>
                )}
              </div>
            </form>
          </div>

          <p className="mt-6 text-sm text-muted-foreground text-center">
            Already have an account?{' '}
            <Link href="/login" className="text-amber-400 hover:text-amber-300 transition-colors font-medium">Sign in →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
